<template>
  <div class="sp-sort">
    <SortMenu
      ref="menu"
      :draft="draftValue"
      :options="options"
      :select-id="selectId"
      :trigger-label="selectedLabel"
      :trigger-prefix="menuTriggerPrefix"
      :disabled="isMenuDisabled"
      :title="dialogTitle"
      :close-text="closeText"
      :caret-image="caretImage"
      :focus-ring="toBoolean(focusRing)"
      :anchor-host="toBoolean(anchorHost)"
      :auto-height="toBoolean(autoHeight)"
      :toggle-test-id="toggleTestId"
      :option-test-id="optionTestId"
      @select="handleSelect"
      @apply="handleApply"
    />
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, toRef, useTemplateRef, watch } from "vue";
import { toBoolean } from "#ce/utils/props";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useExpose } from "#ce/composables/useExpose";
import { formAssociatedHost } from "#ce/framework/utils";
import SortMenu from "./components/SortMenu.ce.vue";
import { useDistanceSortPrompt } from "./composables/useDistanceSortPrompt";
import { useSortOptions } from "./composables/useSortOptions";
import { useSortSelect } from "./composables/useSortSelect";
import { props as propsDefinitions } from "./props.js";

defineOptions(formAssociatedHost());

const props = defineProps(propsDefinitions);

const menu = useTemplateRef("menu");

const { disabled: isDisabled } = useFormControl({ disabled: toRef(props, "disabled") });

const draftValue = ref("");

const boundSource = useSortSelect(() => props.selectId, { exposeValue: false });
const ownedSource = useSortOptions({ exposeValue: false });
const source = computed(() => (props.selectId ? boundSource : ownedSource));
const options = computed(() => source.value.options.value);
const selectedValue = computed(() => source.value.selectedValue.value);
const isBound = computed(() => source.value.isBound.value);
const isMenuDisabled = computed(() => !isBound.value || isDisabled.value);
const { mirrorFormValue, onFormReset } = useForm();
mirrorFormValue(selectedValue);
onFormReset(resetValue);
const { exposeProperties } = useExpose();
exposeProperties({
  value: {
    get: () => selectedValue.value,
    set: setValue,
  },
});
const { shouldPromptFor, promptForLocation, isLocationResolutionEvent } = useDistanceSortPrompt(
  () => props.nearFilterSelector,
);

function handleLocationResolutionChange(event) {
  if (isLocationResolutionEvent(event, selectedValue.value)) {
    menu.value?.close({ restoreFocus: false });
  }
}

function setValue(value) {
  source.value.setValue(value);
}

function resetValue() {
  source.value.reset();
}

useEventListener(document, "change", handleLocationResolutionChange);

const selectedLabel = computed(() => {
  const selectedOption = options.value.find((option) => option.value === selectedValue.value) || options.value[0];

  return selectedOption?.label || "";
});

const menuTriggerPrefix = computed(() => (selectedLabel.value ? props.triggerPrefix : ""));

function syncDraftValue(value) {
  draftValue.value = value;
}

watch(selectedValue, syncDraftValue);

function initializeDraftValue() {
  draftValue.value = selectedValue.value;
  if (shouldPromptFor(selectedValue.value)) {
    requestAnimationFrame(promptForLocation);
  }
}

onMounted(initializeDraftValue);

function handleSelect(value) {
  if (isDisabled.value) {
    return;
  }

  draftValue.value = value;
  applySelection();
}

function handleApply() {
  if (isDisabled.value) {
    return;
  }

  // Done button: re-apply idempotently and skip the location prompt so users
  // who confirmed their distance choice without setting a location don't get
  // the near filter forced open.
  source.value.apply(draftValue.value);
}

function applySelection() {
  source.value.apply(draftValue.value);
  if (shouldPromptFor(draftValue.value)) {
    // Close the sort dialog before opening the near filter — interactions inside
    // the near filter don't bubble to document, so outside-click detection won't
    // auto-close the sort dialog.
    menu.value?.close({ restoreFocus: false });
    promptForLocation();
  }
}
</script>

<style>
:host {
  display: inline-block;
  position: relative;
}
</style>
